import Controller, { OnBeforeCancel, OnBeforeSubmit, OnMount, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { CheckboxAPI, FetchAPI, FormAPI, InputAPI, LocatorAPI, LocatorFieldAPI, ToastService } from '@philips-internal/oswald-ui-components';
import BaseController from '../../../BaseController';
import { FragmentNames, ScorePewsCreateAliases } from '../../../../codegen/index';
import moment from 'moment';
import { ISO_FULL_DATE_FORMAT } from '../../../../constants/date-utils';
import timeService from '../../../../services/time-service';
import { Inject} from '@odinjs/odin';
import InformationBarUtils from '../../../commons/header/information-bar-header-utils';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { Value } from '@philips-internal/oswald-ui-core';
import { LocatorTypes } from '../../../locator/locator-controller';
import { HttpService,  } from '@philips-internal/oswald-ui-core/lib/services';
import { Endpoint } from '../../../../constants/endpoints';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { HealthProfessionalUser } from '../../../../app/authentication';
import { AbortableCloseEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import {DomainType, PersonType, ResponseCreateScorePEWS} from '../../score-types';
import { BooleanUtils } from '../../../../utils/boolean-utils';
import DialogService from '../../../../services/dialog-service';
import { ScoreType } from '../../scores-references/score-reference-controller';
import { ScoreListContext } from '../../scores-list/scores-list-service';

@Controller({ component: ScorePewsCreateAliases.SCORE_PEWS_ADD_LAYOUT, fragment: FragmentNames.SCORE_PEWS_CREATE })
export default class ScorePewsCreateController extends BaseController {

  @Inject(ScorePewsCreateAliases.INPUT_SETUP_DATE)
  setupDateInputApi!: InputAPI;

  @Inject(ScorePewsCreateAliases.FORM_SCORE_PEWS_ADD)
  formApi!: FormAPI;

  @Inject(ScorePewsCreateAliases.FETCH_PROFESSIONAL)
  fetchProfessionalApi!: FetchAPI;

  @Inject(ScorePewsCreateAliases.LOCATOR_FIELD_PROFESSIONAL)
  locatorFieldApi!: LocatorFieldAPI;

  @Inject(ScorePewsCreateAliases.LOCATOR_PROFESSIONAL)
  locatorProfessionalAPI!: LocatorAPI;

  @Inject(ScorePewsCreateAliases.CHECKBOX_NEBULIZATION)
  checkboxNebulizationAPI!: CheckboxAPI;

  @Inject(ScorePewsCreateAliases.CHECKBOX_VOMIT)
  checkboxVomitAPI!: CheckboxAPI;

  @OnMount({ target: ScorePewsCreateAliases.SCORE_PEWS_ADD_LAYOUT })
  handleFormMount(): void {
    ScoreListContext.currentTab = ScoreType.SCORE_PEWS
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();

    InformationBarUtils.collapsedBar = true;
    this.context.set({ encounterId, locator: LocatorTypes.PROFESSIONAL_PERSON, patientId });
    timeService.getCurrentDateTime()
      .then(date => this.setupDateInputApi.setValue(date))
      .catch(() => this.setupDateInputApi.setValue(moment().format(ISO_FULL_DATE_FORMAT)))
  }

  @OnMount({ target: ScorePewsCreateAliases.FIELD_PROFESSIONAL })
  handleMountProfessional(): void {
    const user = foundationContext.getUser() as unknown as HealthProfessionalUser;
    this.locatorFieldApi.setValue(user?.physicalPersonId)
    this.fetchProfessionalApi.setEndpoint(Endpoint.GET_PROFESSIONAL_PERSON);
  }

  @OnMount({ target: ScorePewsCreateAliases.LOCATOR_PROFESSIONAL })
  handleMountProfessionalLocator(event: MountEvent): void {
    const locatorApi = event.target as unknown as LocatorAPI;
    const user = foundationContext.getUser() as unknown as HealthProfessionalUser;
    locatorApi.setValue({
      personCode: user?.physicalPersonId,
      personName: user?.name,
    })
  }

  private releasePEWScore(sequenceNumber: number): void {
    this.formApi.setLoading(true);
    HttpService.put(Endpoint.RELEASE_SCORE_PEWS, { sequenceNumber })
      .then(() => {
        applicationContext.popNavigationItem();
      }).catch((err) => {
        this.showToast(err?.body.errorMessage, ToastLevel.ERROR);
      }).finally(() => {
        this.formApi.setLoading(false);
      });
  }

  private showModalSubmit(idScore: number): void {
    DialogService.open({
      descriptionDialog: 'patient-evolution.release-title-evolution',
      onClickCancel: () => { this.goBack() },
      onClickOk: () => { this.releasePEWScore(idScore) },
      titleDialog: 'patient-evolution.release-evolution',
      titleOnCancel: 'patient-evolution.release-cancel-evolution',
      titleOnOk: 'patient-evolution.release-submit-evolution',
    });
  }

  private createNewPEWSScore(body: Record<string,Value>): void {
    this.formApi.setLoading(true);
    HttpService.post(Endpoint.POST_CREATE_SCORE_PEWS, undefined, {body})
      .then(response => {
        const { idScoreIndexes } = response.body as ResponseCreateScorePEWS;
        this.showModalSubmit(idScoreIndexes);
        this.context.set({ idScoreIndexes, isCreated: true });
      })
      .catch(err => {
        this.showToast(err?.body.errorMessage, ToastLevel.ERROR);
      }).finally(() => this.formApi.setLoading(false));
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'score-pews-create',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }

  private goBack(): void {
    applicationContext.popNavigationItem();
    applicationContext.popNavigationItem();
  }

  @OnBeforeCancel({ target: ScorePewsCreateAliases.FORM_SCORE_PEWS_ADD })
  onCancelForm(event: AbortableCloseEvent): void {
    event.abort();
    this.discardChanges();
  }

  @OnBeforeSubmit({ target: ScorePewsCreateAliases.FORM_SCORE_PEWS_ADD })
  handleBeforeSubmit(): void {
    const isCreated = this.context.get('isCreated');
    const {encounterId, idScoreIndexes} = this.historyService.getCurrentRouteParams();
    if (isCreated) {
      this.showModalSubmit(idScoreIndexes as number);
      return;
    }
    const formData = this.formApi.getRecord();
    const nebulization = BooleanUtils.isPositiveValue(formData.nebulization as boolean);
    const vomit = BooleanUtils.isPositiveValue(formData.vomit as boolean);

    const evaluationDate = moment(formData.dateEvaluation as string).utc().format(ISO_FULL_DATE_FORMAT);
    const cardiovascular = formData.cardiovascular as DomainType;
    const neurologicalStatus = formData.neurologicalStatus as DomainType;
    const respiratory = formData.respiratory as DomainType;
    const person = formData.person as PersonType;
    const body = {
      attendance: encounterId as number,
      cardiovascular: cardiovascular ? cardiovascular.valueDomain : '',
      evaluationDate,
      idScoreIndexes: null,
      nebulization,
      neurologicalStatus: neurologicalStatus ? neurologicalStatus.valueDomain : '',
      professionalCode: person.personCode,
      respiratory: respiratory ? respiratory.valueDomain : '',
      vomit,
    }
    this.createNewPEWSScore(body);
  }

  private discardChanges(): void {
    DialogService.open({
      descriptionDialog: 'clinical-notes.discard-changes',
      onClickCancel: () => {},
      onClickOk: () => {
        applicationContext.popNavigationItem();
        applicationContext.popNavigationItem();
      },
      titleDialog: 'clinical-notes.discard-changes',
      titleOnCancel: 'patient-evolution.cancel',
      titleOnOk: 'clinical-notes.discard-submit'
    });
  }

  @OnUnmount(ScorePewsCreateAliases.SCORE_PEWS_ADD_LAYOUT)
  handleUnmount(): void {
    ScoreListContext.clean();
    ScoreListContext.currentTab = ScoreType.SCORE_PEWS;
  }
}
